import {
  HeadphonesCustomerSupportHumanBold,
  Logout1Bold,
  QuestionCircleBold,
} from '@ha/icons';
import React from 'react';
import { makeStyles } from '@material-ui/core';

import { useIntl } from 'ha/i18n';
import {
  Button,
  Divider,
  Grid,
  ListItemIcon,
  ListItemText,
  MenuList,
  Typography,
} from '@ha/dsl';
import LanguageSwitcher from 'ha/modules/LanguageSwitcher';
import IntercomHandler from 'ha/components/IntercomHandler';

import { usePage } from 'ha/modules/Page/contexts/PageContext';
import { LOGIN, REGISTER } from 'ha/constants/pageNames';
import MobileDropdownMenu from './MobileDropdownMenu';

import MenuItem from './MenuItem';
import { getLearnMenuLinks, getProductsMenuLinks } from '../utils';
import AuthModalActions from '../containers/AuthModalActions';
import AuthenticatedMobileMenuItems from '../containers/AuthenticatedMobileMenuItems.lazy';
import useListYourPlaceCTA from '../hooks/useListYourPlaceCTA';

const useStyles = makeStyles(theme => ({
  languageSwitcher: {
    color: theme.palette.grey[700],
    width: '100%',
  },
  login: {
    margin: '1rem',
  },
  divider: {
    padding: '0 1rem',
    margin: '1rem 0',
  },
  langWrapper: {
    padding: '1rem',
  },
}));

interface Props {
  isAuthenticated: boolean;
  logout: () => void;
  showDrawerMenu: boolean;
}

export const MobileMainMenu: React.FC<Props> = ({
  isAuthenticated,
  logout,
  showDrawerMenu,
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const { T, urlResolver } = intl;
  const { name } = usePage();
  const isAuthPage = name === LOGIN || name === REGISTER;

  const ctaProps = useListYourPlaceCTA();

  return (
    <MenuList disablePadding data-test-locator="Mobile Main Menu">
      {isAuthenticated && <AuthenticatedMobileMenuItems />}
      {!isAuthenticated && !isAuthPage && (
        <AuthModalActions>
          {({ onRegisterClick, onLoginClick }) => (
            <div className={classes.login}>
              <Grid container alignItems="center" spacing={1}>
                <Grid item>
                  <Button
                    data-test-locator="Register button"
                    onClick={onRegisterClick}
                    variant="contained"
                    color="primary"
                  >
                    {T('Sign up')}
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    data-test-locator="Login button"
                    onClick={onLoginClick}
                    variant="outlined"
                    color="primary"
                  >
                    {T('Log in')}
                  </Button>
                </Grid>
              </Grid>
            </div>
          )}
        </AuthModalActions>
      )}
      <Divider />
      {ctaProps.isVisible && (
        <MenuItem to={ctaProps.href} rel={ctaProps.rel} divider>
          <ListItemText>
            <Typography.Paragraph textTransform="uppercase">
              {ctaProps.label}
            </Typography.Paragraph>
          </ListItemText>
        </MenuItem>
      )}
      {showDrawerMenu && (
        <React.Fragment>
          <MobileDropdownMenu
            title={T('navbar_products_button')}
            testId="Mobile Products"
            data={getProductsMenuLinks(intl)}
            hasDivider
          />
          <MenuItem to={urlResolver.getPricingTenantsUrl()} divider>
            <ListItemText>
              <Typography.Paragraph textTransform="uppercase">
                {T('pricing_link')}
              </Typography.Paragraph>
            </ListItemText>
          </MenuItem>
          <MobileDropdownMenu
            title={T('navbar_learn_button')}
            testId="Mobile Learn"
            data={getLearnMenuLinks(intl)}
            hasDivider
          />
        </React.Fragment>
      )}
      <IntercomHandler data-test-locator="Intercom help" key="intercom-help">
        {({ show }) => (
          <MenuItem onClick={show}>
            <ListItemIcon iconSize="small">
              <QuestionCircleBold fontSize="small" />
            </ListItemIcon>
            <ListItemText>{T('Help')}</ListItemText>
          </MenuItem>
        )}
      </IntercomHandler>
      <MenuItem to={urlResolver.getContactUrl()}>
        <ListItemIcon iconSize="small">
          <HeadphonesCustomerSupportHumanBold fontSize="small" />
        </ListItemIcon>
        <ListItemText>{T('Contact us')}</ListItemText>
      </MenuItem>
      {isAuthenticated && (
        <MenuItem onClick={logout}>
          <ListItemIcon iconSize="small">
            <Logout1Bold fontSize="small" />
          </ListItemIcon>
          <ListItemText>{T('Logout')}</ListItemText>
        </MenuItem>
      )}
      <Divider />
      <div className={classes.langWrapper}>
        <Typography.HelperText color="muted">
          {T('Select language')}
        </Typography.HelperText>
        <LanguageSwitcher className={classes.languageSwitcher} kind="full" />
      </div>
    </MenuList>
  );
};

export default MobileMainMenu;
